import { useTheme } from '@mui/material/styles';
import React from 'react';
import AvailabilitySlotValue from './AvailabilitySlotValue';

/**
 * Generic timeline list. Accepts array of `times` with properties:
 * - i: index
 * - time: formatted time label
 * - s0/frees0 etc. occupancy numbers (custom)
 *
 * The `renderSlotRows` prop allows callers to define how many slot rows and
 * how they should be displayed (color etc.). Defaults to single row (s0/frees0).
 */
function TimelineBase({
  times,
  renderSlotRows,
  renderAvailableSlots = true,
  slotWidth = 120,
  showTimeRow = true,
  showSlotRows = true,
  hideTimeRow = false,
}) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const slotRowBg = isLight ? theme.palette.grey[200] : theme.palette.grey[800];
  const hourBarBg = isLight ? theme.palette.grey[300] : theme.palette.common.black;
  const hourLabelColor = isLight ? theme.palette.text.primary : theme.palette.getContrastText(hourBarBg);

  const defaultRenderRows = () => (
    <div style={{ display: 'flex', overflowX: 'visible', marginBottom: '0px' }}>
      {times.map(time => (
        <React.Fragment key={time.i + 's0'}>
          <div
            style={{
              minWidth: `${slotWidth}px`,
              textAlign: 'center',
              backgroundColor: slotRowBg,
              fontWeight: 'bold',
              color: theme.palette.text.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <div
              style={{
                width: '5ch',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '3px',
              }}
            >
              <AvailabilitySlotValue
                count={time.s0}
                freeText={`${time.frees0} frei`}
                isWarning={time.frees0 < 0}
                overrideBlocked={time.overrideBlocked}
              />
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div
      style={{
        position: 'relative',
        zIndex: 150,
      }}
    >
      {showTimeRow && (
        <div
          style={{
            position: 'relative',
            display: 'flex',
            overflowX: 'visible',
            height: hideTimeRow ? 0 : '1.5em',
            visibility: hideTimeRow ? 'hidden' : 'visible',
            pointerEvents: hideTimeRow ? 'none' : 'auto',
            overflow: hideTimeRow ? 'hidden' : 'visible',
          }}
        >
          {times.map(time => (
            <div
              key={time.i}
              style={{
                minWidth: `${slotWidth}px`,
                position: 'relative',
                backgroundColor: hourBarBg,
                borderBottom: isLight ? `1px solid ${theme.palette.divider}` : undefined,
              }}
            >
              {/* Label centered over the grid line at the left edge of this slot */}
              <span
                style={{
                  position: 'absolute',
                  left: 0,
                  transform: 'translateX(-50%)',
                  fontSize: '1.2em',
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                  color: hourLabelColor,
                }}
              >
                {time.time.replace(/^(\d{1,2}):(\d{2})$/, (match, hour, minute) => {
                  return hour.padStart(2, '0') + ':' + minute;
                })}
              </span>
            </div>
          ))}
        </div>
      )}

      {showSlotRows &&
        renderAvailableSlots &&
        (renderSlotRows || defaultRenderRows)({ times, renderAvailableSlots })}
    </div>
  );
}

export default TimelineBase;
